import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X, type LucideIcon } from 'lucide-react';

import { useSidebar } from '../../../contexts/SidebarContext';
import { EncreTone } from '../../bureau/encreTone';
import { BTN_ICON, BTN_PRIMARY, BTN_SECONDARY } from '../../bureau/buttons';
import { ICON_PASTILLE } from '../../bureau/encre';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), audio[controls], video[controls], [tabindex]:not([tabindex="-1"])';

/**
 * Each open panel's return target. A sheet opened from another (account → contact) inherits the
 * first one's: the row clicked in the first panel leaves with it, and the focus must go back to
 * what opened the chain, not to the page's top.
 */
const returnTargets = new WeakMap<Element, HTMLElement>();

/** A field that holds what the user typed: Escape there must not throw it away with the sheet. */
function holdsTyping(target: EventTarget | null): boolean {
  return (target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement) && target.value.trim() !== '';
}

/**
 * A CRM working sheet in Encre (PLAN-ENCRE-OUTILS): the panel on the right, over the page it opens
 * from — the Bureau or a tool. `encre-surface` gives it Encre's tokens and font, dark with the
 * page's theme. A modal: the focus goes into it (without scrolling the page under it: the panel
 * starts off-screen and a plain focus slid the whole app sideways), Tab stays in it, and closing it
 * — the cross, the backdrop, Escape — gives the focus back where it was. Escape in a field that
 * holds a typed request does not close it. Handing over to another sheet (a contact, a deal) is
 * not a close: the focus is not sent back behind the new one, and the new one gives it back where
 * the first was opened from. `focusKey` (the object's id) brings the focus back into the panel when
 * the sheet shows another object. `dirty` (an edit not saved): closing asks first, the edit stays
 * until « Fermer sans enregistrer » (audit du 13/10/2026: Escape dropped a typed edit).
 */
export function EncreSheet({
  open,
  label,
  focusKey,
  dirty = false,
  onClose,
  children,
}: {
  open: boolean;
  /** The dialog's accessible name: « Fiche de Cars du Nord ». */
  label: string;
  focusKey?: string | null;
  /** An edit is not saved: closing asks first. */
  dirty?: boolean;
  onClose: () => void;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLElement | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const [confirming, setConfirming] = useState(false);
  const confirmingRef = useRef(confirming);
  confirmingRef.current = confirming;
  const stay = useRef<HTMLButtonElement>(null);
  // A sheet opened from elsewhere while an edit waits: held, asked, then run on « Fermer sans enregistrer ».
  const pending = useRef<(() => void) | null>(null);
  const leaving = useRef(false);
  const registerLeaveGuard = useSidebar({ optional: true })?.registerLeaveGuard;
  useEffect(() => {
    if (!open || !dirty || !registerLeaveGuard) return;
    return registerLeaveGuard((proceed) => {
      if (leaving.current) return false;
      pending.current = proceed;
      setConfirming(true);
      return true;
    });
  }, [open, dirty, registerLeaveGuard]);

  // Remembered once per opening: the element to give the focus back to (inherited on a hand-over).
  useEffect(() => {
    if (!open) return;
    const active = document.activeElement;
    if (!trigger.current && active instanceof HTMLElement) {
      const host = active.closest('[data-encre-sheet]');
      trigger.current = (host && returnTargets.get(host)) || active;
    }
    if (panel.current && trigger.current) returnTargets.set(panel.current, trigger.current);
  }, [open]);

  useEffect(() => {
    if (open) panel.current?.focus({ preventScroll: true });
  }, [open, focusKey]);

  useEffect(() => {
    if (dirty) return;
    // Nothing waits any more (saved, cancelled, or left for another deal of the same kind).
    setConfirming(false);
    pending.current = null;
    leaving.current = false;
  }, [dirty]);
  useEffect(() => {
    if (confirming) stay.current?.focus({ preventScroll: true });
  }, [confirming]);

  // Both read refs only: stable, so the Escape listener set at opening stays right.
  const closeNow = useCallback(() => {
    const back = trigger.current;
    trigger.current = null;
    setConfirming(false);
    closeRef.current();
    if (back?.isConnected) back.focus({ preventScroll: true });
  }, []);
  const close = useCallback(() => (dirtyRef.current ? setConfirming(true) : closeNow()), [closeNow]);
  // « Fermer sans enregistrer »: what was held goes on (another sheet), else the sheet closes.
  const leaveAnyway = () => {
    leaving.current = true;
    const next = pending.current;
    pending.current = null;
    if (!next) return closeNow();
    setConfirming(false);
    next();
  };
  const stayHere = () => {
    pending.current = null;
    setConfirming(false);
  };

  useEffect(() => {
    leaving.current = false;
    if (!open) {
      trigger.current = null;
      pending.current = null;
      setConfirming(false);
      return;
    }
    const onKey = (e: globalThis.KeyboardEvent) => {
      // The focus fell out of the sheet onto nothing (a removed button): Tab brings it back in, never
      // behind. Focus held elsewhere — the search, the softphone, the menu — is left where it is.
      const lost = !document.activeElement || document.activeElement === document.body;
      if (e.key === 'Tab' && panel.current && lost) {
        e.preventDefault();
        panel.current.focus({ preventScroll: true });
        return;
      }
      if (e.key !== 'Escape' || e.defaultPrevented || e.isComposing || holdsTyping(e.target)) return;
      // Escape on the question goes back to the edit.
      if (confirmingRef.current) stayHere();
      else close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  // Tab and Shift+Tab go round inside the sheet (aria-modal: the page behind is out of reach).
  const keepFocus = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !panel.current) return;
    const items = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === panel.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="absolute inset-0 z-40 bg-black/30"
            aria-hidden="true"
          />
          <motion.div
            key="panel"
            ref={panel}
            data-encre-sheet=""
            role="dialog"
            aria-modal="true"
            aria-label={label}
            tabIndex={-1}
            onKeyDown={keepFocus}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 260 }}
            className="encre-surface absolute right-0 top-0 z-50 flex h-full w-full max-w-[600px] flex-col shadow-pop focus:outline-none"
          >
            <div className="flex shrink-0 justify-end px-3 pt-3">
              <button type="button" onClick={close} className={BTN_ICON} aria-label="Fermer la fiche">
                <X size={18} strokeWidth={1.75} aria-hidden="true" />
              </button>
            </div>
            {confirming && (
              <div role="alertdialog" aria-label="Modifications non enregistrées" className="mx-6 mb-3 rounded-2xl bg-desk-card p-4 shadow-lift sm:mx-8">
                <p className="text-sm text-desk-strong">Tes modifications ne sont pas enregistrées.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button ref={stay} type="button" onClick={stayHere} className={BTN_PRIMARY}>
                    Continuer la saisie
                  </button>
                  <button type="button" onClick={leaveAnyway} className={BTN_SECONDARY}>
                    Fermer sans enregistrer
                  </button>
                </div>
              </div>
            )}
            {/* `pb-10` is cancelled by `SheetFormFooter`'s `-bottom-10`: change both together. */}
            <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-10 sm:px-8">
              <EncreTone>{children}</EncreTone>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

/**
 * A block of a sheet (mock-up « Fiche »): a thin rule, the title in 14 px with an optional count, the
 * content. An empty block says so in a line rather than disappearing — the sheet's shape stays.
 */
export function SheetBlock({
  title,
  count,
  action,
  children,
}: {
  title: string;
  count?: number;
  /** A gesture on the right of the title (« Tout voir »). */
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="border-t border-desk-line pb-6 pt-5" aria-label={title}>
      <div className="mb-3 flex items-baseline gap-3">
        <h2 className="text-sm font-medium text-desk-ink">{title}</h2>
        {count !== undefined && count > 0 && <span className="text-sm text-desk-muted">{count}</span>}
        <span className="flex-1" />
        {action}
      </div>
      {children}
    </section>
  );
}

/** An icon in its blue pastille, the Bureau's way (a mission, a deal, an exchange). */
export function Pastille({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <span aria-hidden="true" className={ICON_PASTILLE}>
      <Icon size={14} strokeWidth={1.9} />
    </span>
  );
}

/** The line an empty block says (« Aucune opportunité en cours. »). */
export function SheetEmpty({ children }: { children: ReactNode }) {
  return <p className="text-sm text-desk-muted">{children}</p>;
}

/** What a block shows before its rows arrive: a quiet bar, never a false « Aucun… ». */
export function SheetLoading() {
  return <div className="h-10 animate-pulse rounded-[10px] bg-desk-subtle" aria-busy="true" />;
}

/** A re-read that failed (after a save): the sheet keeps the last version read, and says so. */
export function ReReadError({ error }: { error: string }) {
  return (
    <p role="alert" className="mb-6 text-sm text-red-600 dark:text-red-400">
      Relecture impossible : {error}. La fiche montre la dernière version lue.
    </p>
  );
}
